import { useQuery } from '@tanstack/react-query'

import type { KeyPickupTravelCostRequestV2DTO, KeyPickupTravelCostV2DTO } from 'models/purchaseFlowRevamp'
import { EntityKeys, QueryType } from 'utils/reactQuery'
import { useAPIV2 } from 'utils/API'
import { usePfStore } from 'components/pages/PurchaseFlowRevamp/store'
import { usePfSessionWorkspaceId } from 'components/pages/PurchaseFlowRevamp/routing/usePfSessionWorkspaceId'

enum Endpoints {
  KEY_PICKUP_TRAVEL_COST = '/workspace/{workspaceId}/sessions/{sessionId}/key-pickup/travel-cost',
}

/**
 * React Query hook — the travel cost the order will bill for collecting the keys at `pickup`
 * (`POST …/sessions/{id}/key-pickup/travel-cost`), computed by the same function placement bills with.
 *
 * Keyed on the address and its coordinates, so a new pickup re-quotes and an unchanged one does not.
 */
export function useKeyPickupTravelCost(pickup: KeyPickupTravelCostRequestV2DTO | null) {
  const api = useAPIV2<Endpoints>()
    const sessionId = usePfStore((s) => s.draft.sessionId)
  const ws = usePfSessionWorkspaceId()

  return useQuery({
    queryKey: [EntityKeys.PF_REVAMP_KEY_PICKUP_TRAVEL_COST, QueryType.GET_ONE, { ws, sessionId, pickup }],
    queryFn: async (): Promise<KeyPickupTravelCostV2DTO> => {
      if (!ws || !sessionId || !pickup) throw new Error('key pickup travel cost called without a pickup')
      return api.post<KeyPickupTravelCostV2DTO>(
        Endpoints.KEY_PICKUP_TRAVEL_COST,
        { workspaceId: ws, sessionId },
        pickup,
        false
      )
    },
    enabled: Boolean(ws && sessionId && pickup),
    staleTime: 5 * 60_000,
    retry: 1,
  })
}
